import { memo, useCallback, useMemo } from 'react'
import { Box, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import { ChartAlertSnapshot } from '../alertModal.typings'
import { AlertExpression, AlertExpression_Type, Condition_SecurityAlertCondition_Operator } from '../../../proto/generated/nexus'
import {
    decodeAlertExpressionTimeBucketKey,
    encodeAlertExpressionTimeBucketKey,
    getOperatorSource
} from '../../../util/alerts'
import { TimeBucket_TimeSeries } from '../../../proto/generated/common'
import { getTimeBucketDisplayName } from '../../../util/timeBuckets'
import { useStyles } from './styles'
import { alertModalMenuProps } from '../alertModal'

const ALERT_INTERVAL_SAME_AS_CHART = '__same_as_chart__'

export type AlertCompareTimeBucketRowProps = {
    chartSnapshot: ChartAlertSnapshot
    expression: AlertExpression
    operator: Condition_SecurityAlertCondition_Operator | undefined
    onExpressionChange: (expression: AlertExpression, change: keyof AlertExpression) => void
    className?: string
    /** When true, omit the built-in "Interval" caption (e.g. parent renders a left-column label). */
    hideLabel?: boolean
}

export const AlertCompareTimeBucketRow = memo(function AlertCompareTimeBucketRow({
    chartSnapshot,
    expression,
    operator,
    onExpressionChange,
    className,
    hideLabel = false
}: AlertCompareTimeBucketRowProps) {
    const { classes } = useStyles()
    const { securityList } = chartSnapshot

    const intervalSelectValue = useMemo(() => {
        if (!expression.timeBucket) return ALERT_INTERVAL_SAME_AS_CHART
        return encodeAlertExpressionTimeBucketKey(expression.timeBucket)
    }, [expression.timeBucket])

    const intervalMenuKeys = useMemo(() => {
        const keys = new Set<string>()
        const buckets = chartSnapshot.availableTimeBuckets ?? []
        buckets.forEach((tb) => {
            if (
                tb.timeSeries === TimeBucket_TimeSeries.UNRECOGNIZED ||
                tb.timeSeries === TimeBucket_TimeSeries.TIME_SERIES_UNKNOWN
            ) {
                return
            }
            keys.add(encodeAlertExpressionTimeBucketKey(tb))
        })
        return [...keys]
    }, [chartSnapshot.availableTimeBuckets])

    const defaultCompareSource = useMemo(
        () => getOperatorSource(operator ?? Condition_SecurityAlertCondition_Operator.CROSSING),
        [operator]
    )

    const onIntervalChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const v = event.target.value
            const nextTb = v === ALERT_INTERVAL_SAME_AS_CHART ? undefined : decodeAlertExpressionTimeBucketKey(v)
            if (v !== ALERT_INTERVAL_SAME_AS_CHART && !nextTb) return
            // Pattern alerts keep PATTERN + payload; only the candle interval override changes.
            if (expression.type === AlertExpression_Type.PATTERN) {
                onExpressionChange({ ...expression, timeBucket: nextTb }, 'timeBucket')
                return
            }
            onExpressionChange(
                {
                    ...expression,
                    type: AlertExpression_Type.SIMPLE_SECURITY,
                    simpleSecurity: expression.simpleSecurity ?? {
                        security: { exchange: securityList[0].exchange, code: securityList[0].code },
                        source: defaultCompareSource
                    },
                    timeBucket: nextTb,
                    spreadSecurity: undefined,
                    indicator: undefined,
                    fixedValue: undefined
                },
                'timeBucket'
            )
        },
        [expression, onExpressionChange, securityList, defaultCompareSource]
    )

    const intervalRenderValue = useCallback(
        (v: string) => {
            if (v === ALERT_INTERVAL_SAME_AS_CHART) {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                        <Typography component="span" variant="body2">
                            Same as chart
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary">
                            {getTimeBucketDisplayName(chartSnapshot.timeBucket)}
                        </Typography>
                    </Box>
                )
            }
            const tb = decodeAlertExpressionTimeBucketKey(v)
            return (
                <Typography variant="body2" color="text.secondary">
                    {tb ? getTimeBucketDisplayName(tb) : v}
                </Typography>
            )
        },
        [chartSnapshot.timeBucket]
    )

    return (
        <Box className={className}>
            {!hideLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Interval
                </Typography>
            )}
            <Select<string>
                className={classes.fullWidth}
                size="small"
                value={intervalSelectValue}
                onChange={onIntervalChange}
                displayEmpty
                renderValue={intervalRenderValue}
                MenuProps={alertModalMenuProps}
            >
                <MenuItem value={ALERT_INTERVAL_SAME_AS_CHART}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            gap: 2,
                            pr: 0.5
                        }}
                    >
                        <Typography variant="body2">Same as chart</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {getTimeBucketDisplayName(chartSnapshot.timeBucket)}
                        </Typography>
                    </Box>
                </MenuItem>
                {intervalMenuKeys.map((key) => {
                    const tb = decodeAlertExpressionTimeBucketKey(key)
                    if (!tb) return null
                    return (
                        <MenuItem key={key} value={key}>
                            {getTimeBucketDisplayName(tb)}
                        </MenuItem>
                    )
                })}
            </Select>
        </Box>
    )
})
